import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {EAccountConfirmationStatus} from '@app/auth-config/models/auth-state.model';
import {IResetPasswordForm} from '@app/auth-config/models/reset-password-form.model';
import {IResetPasswordRequest} from '@app/auth-config/models/reset-password.model';
import {resetPassword, resetPasswordStateReset} from '@app/auth-config/state/auth.actions';
import {selectResetPasswordStatus} from '@app/auth-config/state/auth.selector';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {PlatformStorage} from '@app/core/tokens/platform-storage.token';
import {OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex} from '@app/shared/constants';
import {Store} from '@ngrx/store';
import {Subject, takeUntil, tap} from 'rxjs';
import { LoginInfoComponent } from '../../../shared/components/login-info/login-info.component';
import { CustomPasswordComponent } from '../../../shared/components/custom-password/custom-password.component';
import { Bind } from 'primeng/bind';
import { Message } from 'primeng/message';
import { Password } from 'primeng/password';
import { Button } from 'primeng/button';
import { LanguageSelectorComponent } from '../../../shared/components/language-selector/language-selector.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [
        LoginInfoComponent,
        FormsModule,
        ReactiveFormsModule,
        CustomPasswordComponent,
        Bind,
        Message,
        Password,
        Button,
        LanguageSelectorComponent,
        TranslatePipe,
    ],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly storage = inject(PlatformStorage);

  resetPasswordForm: FormGroup<IResetPasswordForm>;
  token = '';
  email = '';
  resetInProgress = false;
  private readonly unsubscribe$ = new Subject<void>();
  constructor() {
    this.resetPasswordForm = this.formBuilder.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.pattern(RegExp(OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex)),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      {validators: [this.passwordMatchValidator]}
    );
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control?.get('password')?.value;
    const confirmPassword = control?.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      control?.get('confirmPassword')?.setErrors({passwordMismatch: true});
    } else if (!confirmPassword) {
      control?.get('confirmPassword')?.setErrors({required: true});
    } else {
      control?.get('confirmPassword')?.setErrors(null);
    }
    return null;
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((params: Params) => {
          this.token = decodeURIComponent(params['token']);
          this.email = params['email'];
        })
      )
      .subscribe();

    this.store
      .select(selectResetPasswordStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: EAccountConfirmationStatus) => {
          if (status === EAccountConfirmationStatus.Confirmed || status === EAccountConfirmationStatus.Error) {
            this.resetInProgress = false;
            if (status == EAccountConfirmationStatus.Confirmed) {
              this.storage.removeItem(StorageKeys.accessToken);
              this.router.navigate(['/']);
            }
          }
        })
      )
      .subscribe();
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSubmit() {
    if (this.resetPasswordForm?.valid) {
      this.resetInProgress = true;
      this.store.dispatch(resetPasswordStateReset());
      this.store.dispatch(resetPassword({request: this.createResetPasswordRequest()}));
    }
  }

  private createResetPasswordRequest(): IResetPasswordRequest {
    return {
      newPassword: this.resetPasswordForm.get('password')?.value ?? '',
      email: this.email,
      token: this.token,
    } as IResetPasswordRequest;
  }
}
